import { type StateCreator } from 'zustand';

export type ToolCallStatusEntry = {
  toolCallId: string;
  toolCallName: string;
  status: 'working' | 'completed' | 'error';
};

export type ToolCallStatusSlice = {
  /** Tool call statuses keyed by thread ID. */
  byThread: Record<string, ToolCallStatusEntry[]>;
  /** Adds a tool call entry with 'working' status. Idempotent — skips if entry already exists. */
  startToolCall: (threadId: string, toolCallId: string, toolCallName: string) => void;
  /** Updates an existing tool call entry to 'completed' status. */
  completeToolCall: (threadId: string, toolCallId: string) => void;
  /** Marks all 'working' tool calls for a thread as 'error'. */
  failToolCalls: (threadId: string) => void;
};

export type CreateToolCallStatusSlice = () => StateCreator<ToolCallStatusSlice, [], [], ToolCallStatusSlice>;

export const createToolCallStatusSlice: CreateToolCallStatusSlice = () => (set, get) => ({
  byThread: {},
  completeToolCall: (threadId, toolCallId) => {
    const existing = get().byThread[threadId];

    if (!existing) {
      return;
    }

    set({
      byThread: {
        ...get().byThread,
        [threadId]: existing.map((entry) =>
          entry.toolCallId === toolCallId ? { ...entry, status: 'completed' as const } : entry,
        ),
      },
    });
  },
  failToolCalls: (threadId) => {
    const existing = get().byThread[threadId];

    if (!existing) {
      return;
    }

    const hasWorking = existing.some((entry) => entry.status === 'working');

    if (!hasWorking) {
      return;
    }

    set({
      byThread: {
        ...get().byThread,
        [threadId]: existing.map((entry) =>
          entry.status === 'working' ? { ...entry, status: 'error' as const } : entry,
        ),
      },
    });
  },
  startToolCall: (threadId, toolCallId, toolCallName) => {
    const existing = get().byThread[threadId] ?? [];

    if (existing.some((entry) => entry.toolCallId === toolCallId)) {
      return;
    }
    set({
      byThread: {
        ...get().byThread,
        [threadId]: [...existing, { status: 'working' as const, toolCallId, toolCallName }],
      },
    });
  },
});
